/**
 * NotificationPopup - Displays important alerts to users
 * Used for non-blocking but important notifications throughout the designer
 */
export default class NotificationPopup {
    /**
     * Create a new notification popup
     * 
     * @param {object} options - Configuration options
     */
    constructor(options = {}) {
        this.options = {
            title: 'Notification',
            message: '',
            primaryButton: null, // { text, action, className }
            secondaryButton: null, // { text, action, className }
            closeOnBackdropClick: true,
            closeOnEscape: true,
            autoClose: false,
            autoCloseDelay: 5000,
            overlayClass: '',
            popupClass: '',
            ...options
        };
        
        this.element = null;
        this.overlay = null;
        this.isVisible = false;
        this.autoCloseTimer = null;
        
        this.initialize();
    }
    
    /**
     * Initialize the popup
     */
    initialize() {
        // Create overlay element
        this.overlay = document.createElement('div');
        this.overlay.className = `stc-notification-overlay ${this.options.overlayClass}`;
        
        // Create popup element
        this.element = document.createElement('div');
        this.element.className = `stc-notification-popup ${this.options.popupClass}`;
        
        // Update content
        this.updateContent();
        
        // Add to overlay
        this.overlay.appendChild(this.element);
        
        // Bind events
        this.bindEvents();
    }
    
    /**
     * Update popup content
     * 
     * @param {object} options - New content options
     */
    updateContent(options = {}) {
        // Update options if provided
        if (Object.keys(options).length > 0) {
            this.options = { ...this.options, ...options };
        }
        
        // Generate HTML
        let html = '';
        
        // Add title
        if (this.options.title) {
            html += `<div class="stc-notification-title">${this.options.title}</div>`;
        }
        
        // Add message
        if (this.options.message) {
            html += `<div class="stc-notification-message">${this.options.message}</div>`;
        }
        
        // Add buttons
        if (this.options.primaryButton || this.options.secondaryButton) {
            html += '<div class="stc-notification-actions">';
            
            // Secondary button
            if (this.options.secondaryButton) {
                html += `
                    <button class="stc-notification-button stc-notification-secondary ${this.options.secondaryButton.className || ''}">
                        ${this.options.secondaryButton.text}
                    </button>
                `;
            }
            
            // Primary button
            if (this.options.primaryButton) {
                html += `
                    <button class="stc-notification-button stc-notification-primary ${this.options.primaryButton.className || ''}">
                        ${this.options.primaryButton.text}
                    </button>
                `;
            }
            
            html += '</div>';
        }
        
        // Update element content
        this.element.innerHTML = html;
        
        // Rebind button events
        this.bindButtonEvents();
    }
    
    /**
     * Bind event listeners
     */
    bindEvents() {
        // Backdrop click
        if (this.options.closeOnBackdropClick) {
            this.overlay.addEventListener('click', (e) => {
                if (e.target === this.overlay) {
                    this.hide();
                }
            });
        }
        
        // Escape key
        if (this.options.closeOnEscape) {
            document.addEventListener('keydown', this.handleKeyDown.bind(this));
        }
        
        // Bind button events
        this.bindButtonEvents();
    }
    
    /**
     * Bind button event listeners
     */
    bindButtonEvents() {
        // Primary button
        const primaryBtn = this.element.querySelector('.stc-notification-primary');
        if (primaryBtn && this.options.primaryButton) {
            primaryBtn.addEventListener('click', () => {
                if (typeof this.options.primaryButton.action === 'function') {
                    this.options.primaryButton.action();
                }
                this.hide();
            });
        }
        
        // Secondary button
        const secondaryBtn = this.element.querySelector('.stc-notification-secondary');
        if (secondaryBtn && this.options.secondaryButton) {
            secondaryBtn.addEventListener('click', () => {
                if (typeof this.options.secondaryButton.action === 'function') {
                    this.options.secondaryButton.action();
                }
                this.hide();
            });
        }
    }
    
    /**
     * Handle keydown events
     * 
     * @param {Event} e - Keydown event
     */
    handleKeyDown(e) {
        if (this.isVisible && e.key === 'Escape') {
            this.hide();
        }
    }
    
    /**
     * Show the popup
     */
    show() {
        if (this.isVisible) return;
        
        // Add to DOM
        document.body.appendChild(this.overlay);
        
        // Add active class after a small delay (for animation)
        setTimeout(() => {
            this.overlay.classList.add('is-active');
        }, 10);
        
        this.isVisible = true;
        
        // Set up auto-close if enabled
        if (this.options.autoClose) {
            this.autoCloseTimer = setTimeout(() => {
                this.hide();
            }, this.options.autoCloseDelay);
        }
    }
    
    /**
     * Hide the popup
     */
    hide() {
        if (!this.isVisible) return;
        
        // Remove active class first (for animation)
        this.overlay.classList.remove('is-active');
        
        // Remove from DOM after animation
        setTimeout(() => {
            if (this.overlay.parentNode) {
                this.overlay.parentNode.removeChild(this.overlay);
            }
        }, 300); // Match transition duration
        
        this.isVisible = false;
        
        // Clear auto-close timer
        if (this.autoCloseTimer) {
            clearTimeout(this.autoCloseTimer);
            this.autoCloseTimer = null;
        }
    }
    
    /**
     * Update popup options
     * 
     * @param {object} options - New options
     */
    update(options = {}) {
        this.updateContent(options);
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Remove event listeners
        document.removeEventListener('keydown', this.handleKeyDown.bind(this));
        
        // Clear auto-close timer
        if (this.autoCloseTimer) {
            clearTimeout(this.autoCloseTimer);
            this.autoCloseTimer = null;
        }
        
        // Remove from DOM
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
        
        this.element = null;
        this.overlay = null;
    }
}